import React, { useState, useEffect, useRef } from 'react';
import { Send, Plus, Trash2, X, MessageSquare, LogIn, Minimize2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatDrawer = ({ isOpen, onClose }) => {
  const { user, idToken, isAuthenticated } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [demoQuestionCount, setDemoQuestionCount] = useState(0);
  const [showDemoLimit, setShowDemoLimit] = useState(false);
  const messagesEndRef = useRef(null);
  
  const DEMO_QUESTION_LIMIT = 2;

  // Auto scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load sessions on mount if authenticated
  useEffect(() => {
    if (isAuthenticated && isOpen) {
      loadSessions();
    }
  }, [isAuthenticated, isOpen]);

  const loadSessions = async () => {
    try {
      const response = await axios.get(`${API}/chat/sessions`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      if (response.data.success) {
        setSessions(response.data.sessions);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/chat/sessions/${sessionId}`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      if (response.data.success) {
        setCurrentSession(response.data.session);
        setMessages(response.data.session.messages || []);
        setShowSessions(false);
      }
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Failed to load conversation');
    }
  };

  const startNewConversation = () => {
    setCurrentSession(null);
    setMessages([]);
    setInputMessage('');
    setShowSessions(false);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    // Check demo limit for non-authenticated users
    if (!isAuthenticated && demoQuestionCount >= DEMO_QUESTION_LIMIT) {
      setShowDemoLimit(true);
      return;
    }

    const userMessage = inputMessage.trim();
    setInputMessage('');

    // Add user message to UI immediately
    const tempUserMsg = {
      id: Date.now().toString(),
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);
    setIsLoading(true);

    try {
      // Demo mode for non-authenticated users
      if (!isAuthenticated) {
        setDemoQuestionCount(prev => prev + 1);
        
        // Simulate AI thinking delay
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Demo response that's helpful but encourages contact
        const demoResponse = getDemoResponse(demoQuestionCount + 1);
        
        const assistantMsg = {
          id: Date.now().toString() + '1',
          role: 'assistant',
          content: demoResponse,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, assistantMsg]);
        
        // Show limit message after 2nd question
        if (demoQuestionCount + 1 >= DEMO_QUESTION_LIMIT) {
          setTimeout(() => setShowDemoLimit(true), 500);
        }
      } else {
        // Authenticated user - full functionality
        const response = await axios.post(`${API}/chat/message`, {
          message: userMessage,
          session_id: currentSession?.id
        }, {
          headers: {
            Authorization: `Bearer ${idToken}`
          }
        });

        if (response.data.success) {
          // Add assistant response
          const assistantMsg = {
            id: Date.now().toString() + '1',
            role: 'assistant',
            content: response.data.response,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, assistantMsg]);

          // Update current session
          if (!currentSession) {
            // New session created
            await loadSessions();
            await loadSession(response.data.session_id);
          } else {
            // Existing session - reload to get updated messages
            await loadSession(currentSession.id);
            await loadSessions(); // Refresh sidebar
          }
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
      // Remove the temporary user message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempUserMsg.id));
    } finally {
      setIsLoading(false);
    }
  };
  
  const getDemoResponse = (questionNumber) => {
    const responses = [
      "That's a great question! Based on your needs, there are several architectural approaches we could explore. At Westbay Group, we specialize in designing scalable, secure cloud solutions tailored to your specific requirements.\n\n**To provide you with detailed recommendations and a comprehensive strategy, I'd encourage you to:**\n\n• **Contact us at hello@westbay.io** for a personalized consultation\n• **Sign in** to access our full AI-powered architecture guidance\n\nOur team has helped numerous clients solve similar challenges with proven, production-ready solutions.",
      
      "Excellent follow-up! This requires a more detailed analysis of your specific infrastructure, compliance requirements, and scalability goals.\n\n**You've reached the demo limit (2 questions). To continue our conversation and get actionable solutions:**\n\n✅ **Sign in** to unlock unlimited AI-powered guidance\n✅ **Email us at hello@westbay.io** to discuss your project\n✅ **Schedule a consultation** for personalized recommendations\n\nAs a veteran-owned firm, we're committed to delivering enterprise-grade solutions with the precision and reliability you'd expect from military standards. Let's discuss how we can help you succeed."
    ];
    
    return responses[questionNumber - 1] || responses[1];
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;

    try {
      await axios.delete(`${API}/chat/sessions/${sessionId}`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (currentSession?.id === sessionId) {
        startNewConversation();
      }
      toast.success('Conversation deleted');
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Failed to delete conversation');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="chat-drawer-backdrop" onClick={onClose} />
      
      {/* Drawer */}
      <div className="chat-drawer">
        {/* Drawer Header */}
        <div className="chat-drawer-header">
          <div className="chat-drawer-title">
            <MessageSquare size={24} />
            <div>
              <h2>Poser AI™ <span className="patent-badge-inline">Patent Pending</span></h2>
              <p>Your Cloud Solutions Architect - Ask about security, DevOps, and AI</p>
            </div>
          </div>
          <div className="chat-drawer-actions">
            {isAuthenticated && sessions.length > 0 && (
              <button 
                onClick={() => setShowSessions(!showSessions)} 
                className="drawer-sessions-btn"
                title="View conversations"
              >
                <MessageSquare size={20} />
                <span>{sessions.length}</span>
              </button>
            )}
            {isAuthenticated && (
              <button 
                onClick={startNewConversation} 
                className="drawer-new-btn"
                title="New conversation"
              >
                <Plus size={20} />
              </button>
            )}
            <button onClick={onClose} className="drawer-close-btn" title="Close Poser AI">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Sessions Panel (if authenticated and toggled) */}
        {isAuthenticated && showSessions && (
          <div className="drawer-sessions-panel">
            <div className="drawer-sessions-header">
              <h3>Conversations</h3>
              <button onClick={() => setShowSessions(false)} className="close-sessions-btn">
                <X size={20} />
              </button>
            </div>
            <div className="drawer-sessions-list">
              {sessions.map(session => (
                <div
                  key={session.id}
                  className={`drawer-session-item ${currentSession?.id === session.id ? 'active' : ''}`}
                  onClick={() => loadSession(session.id)}
                >
                  <div className="session-content">
                    <div className="session-title">{session.title}</div>
                    <div className="session-date">
                      {new Date(session.updatedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={(e) => deleteSession(session.id, e)}
                    className="delete-session-btn"
                    title="Delete conversation"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messages Area */}
        <div className="chat-drawer-messages">
          {messages.length === 0 ? (
            <div className="drawer-welcome-message">
              <div className="poser-ai-avatar">
                <img src="/poser-ai-hero.png" alt="Poser AI" />
              </div>
              <h3>Welcome to Poser AI™ <span className="patent-small">Patent Pending</span></h3>
              {!isAuthenticated ? (
                <>
                  <div className="demo-badge">
                    🎯 Demo Mode - Try 2 free questions
                  </div>
                  <p>Sign in for unlimited AI-powered guidance, or try the demo with 2 free questions!</p>
                  <p className="poser-tagline">Powered by AWS Bedrock & Claude 4.5 • Backed up by human interaction</p>
                </>
              ) : (
                <>
                  <p>Your Cloud Solutions Architect assistant. Ask me anything about cloud security, DevOps, AI development, and infrastructure!</p>
                  <p className="poser-tagline">Powered by AWS Bedrock & Claude 4.5 • Backed up by human interaction</p>
                </>
              )}
              {!isAuthenticated && (
                <button onClick={() => window.location.href = '/login'} className="drawer-login-btn">
                  <LogIn size={18} />
                  <span>Sign In for Full Access</span>
                </button>
              )}
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <div key={message.id} className={`drawer-message ${message.role}`}>
                  <div className="message-avatar">
                    {message.role === 'user' ? (
                      '👤'
                    ) : (
                      <img src="/poser-ai-hero.png" alt="Poser AI" className="avatar-vangogh" />
                    )}
                  </div>
                  <div className="message-content">
                    <div className="message-text">{message.content}</div>
                    <div className="message-time">
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="drawer-message assistant">
                  <div className="message-avatar">
                    <img src="/poser-ai-hero.png" alt="Poser AI" className="avatar-vangogh" />
                  </div>
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {showDemoLimit && !isAuthenticated && (
            <div className="demo-limit-message">
              <h4>Demo limit reached</h4>
              <p>Sign in to continue with Poser AI™ and get unlimited AI-powered guidance!</p>
              <button onClick={() => window.location.href = '/login'} className="drawer-login-btn">
                <LogIn size={18} />
                <span>Sign In</span>
              </button>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Form */}
        <form onSubmit={sendMessage} className="chat-drawer-input">
          <Textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              !isAuthenticated 
                ? `Try the demo! Ask about cloud security or AI development... (${DEMO_QUESTION_LIMIT - demoQuestionCount} questions left)`
                : "Ask about cloud security, AI development, custom tooling, or technical architecture..."
            }
            className="drawer-message-input"
            rows={2}
            disabled={isLoading || (!isAuthenticated && demoQuestionCount >= DEMO_QUESTION_LIMIT)}
          />
          <button
            type="submit"
            className="drawer-send-button"
            disabled={!inputMessage.trim() || isLoading || (!isAuthenticated && demoQuestionCount >= DEMO_QUESTION_LIMIT)}
          >
            <Send size={20} />
          </button>
        </form>
      </div>
    </>
  );
};

export default ChatDrawer;
